import { supabase } from "@/integrations/supabase/client";

export type CouponPreview =
  | {
      valid: true;
      code: string;
      discountType: "percent" | "amount";
      percentOff: number | null;
      amountOff: number | null;
      currency: "MXN" | "USD" | null;
      description: string | null;
    }
  | { valid: false; reason: string };

type CouponRow = {
  id: string;
  code: string;
  description: string | null;
  discount_type: "percent" | "amount";
  percent_off: number | null;
  amount_off: number | null;
  currency: "MXN" | "USD" | null;
  pathway: "basic" | "intermediate" | "advanced" | null;
  starts_at: string | null;
  expires_at: string | null;
  max_redemptions: number | null;
  times_redeemed: number;
  is_active: boolean;
};

function normalizeCode(code: string) {
  return code.trim().toUpperCase();
}

function checkCoupon(
  coupon: CouponRow | null,
  context: { pathway: string; currency: "MXN" | "USD" },
): { ok: true; coupon: CouponRow } | { ok: false; reason: string } {
  if (!coupon) return { ok: false, reason: "not_found" };
  if (!coupon.is_active) return { ok: false, reason: "inactive" };

  const now = Date.now();
  if (coupon.starts_at && new Date(coupon.starts_at).getTime() > now) {
    return { ok: false, reason: "not_started" };
  }
  if (coupon.expires_at && new Date(coupon.expires_at).getTime() < now) {
    return { ok: false, reason: "expired" };
  }
  if (coupon.max_redemptions !== null && coupon.times_redeemed >= coupon.max_redemptions) {
    return { ok: false, reason: "exhausted" };
  }
  if (coupon.pathway && coupon.pathway !== context.pathway) {
    return { ok: false, reason: "pathway" };
  }
  if (
    coupon.discount_type === "amount" &&
    coupon.currency &&
    coupon.currency !== context.currency
  ) {
    return { ok: false, reason: "currency" };
  }
  return { ok: true, coupon };
}

export async function previewCoupon({
  data,
}: {
  data: { applicationId: string; code: string };
}): Promise<CouponPreview> {
  if (!/^[0-9a-fA-F-]{36}$/.test(data.applicationId)) throw new Error("Invalid input");
  if (typeof data.code !== "string" || data.code.trim().length < 2 || data.code.length > 40) {
    throw new Error("Invalid input");
  }
  const auth = await supabase.auth.getUser();
  if (!auth.data.user) return { valid: false, reason: "not_found" };

  const { data: application, error: appError } = await supabase
    .from("applications")
    .select("id, pathway, currency")
    .eq("id", data.applicationId)
    .maybeSingle();
  if (appError || !application) return { valid: false, reason: "not_found" };

  const code = normalizeCode(data.code);
  const { data: couponRow, error: couponError } = await supabase
    .from("coupons")
    .select("*")
    .ilike("code", code)
    .maybeSingle();

  if (couponError && !couponRow) {
    return { valid: false, reason: "not_found" };
  }

  const result = checkCoupon(couponRow as CouponRow | null, {
    pathway: application.pathway,
    currency: application.currency as "MXN" | "USD",
  });
  if (!result.ok) return { valid: false, reason: result.reason };

  return {
    valid: true,
    code: normalizeCode(result.coupon.code),
    discountType: result.coupon.discount_type,
    percentOff: result.coupon.percent_off,
    amountOff: result.coupon.amount_off === null ? null : Number(result.coupon.amount_off),
    currency: result.coupon.currency,
    description: result.coupon.description,
  };
}
